import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import humanizeDuration from "humanize-duration";
import ReactMarkdown from "react-markdown";
import * as Sentry from '@sentry/react';


interface ApiExperimentAction {
  meta?: Record<string, unknown>;
  type?: string;
  title?: string;
  description?: string;
}

interface ApiExperimentDay {
  title?: string;
  actions?: ApiExperimentAction[];
  description?: string;
}

interface ApiExperimentProtocol {
  days?: ApiExperimentDay[];
  [key: string]: unknown;
}

interface ExperimentProtocolPreviewProps {
  protocolData: ApiExperimentProtocol;
  className?: string;
}

const getModelTime = (modelTime: string | undefined) => {
  if (modelTime) {
    try {
      const [hours, minutes] = modelTime.split(":");
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch (err) {
              Sentry.captureException(err);
      return modelTime;
    }
  }
  return "";
};

// Get icon based on step type
const getStepIcon = (type: string | undefined) => {
  switch (type?.toLowerCase()) {
    case "product":
      return "/images/icons/consume.svg";
    case "satiety":
      return "/images/icons/satiety.svg";
    case "fasting":
      return "/images/icons/watch.svg";
    case "review":
      return "/images/icons/video.svg";
    default:
      return null;
  }
};

// Get additional instructions based on step type and meta data
const getStepInstructions = (action: ApiExperimentAction) => {
  // Check if instructions are provided in meta data
  if (action.meta && action.meta.instructions) {
    const instructions = action.meta.instructions;
    if (Array.isArray(instructions)) {
      return instructions;
    }
    if (typeof instructions === "string") {
      return [instructions];
    }
  }

  // Fall back to type-based instructions
  if (action.type?.toLowerCase() === "consume") {
    return [
      "No eating",
      "Avoid consuming any sweetened beverages, including tea, coffee, soda, alcohol, or juice.",
      "No strenuous exercise",
      "Coffee/tea without sugar or milk are OK",
      "Drinking water is OK",
      "Walking is OK",
    ];
  }
  return [];
};

// Convert time string (HH:MM) to milliseconds
const timeStringToMs = (timeString: string | undefined): number => {
  if (!timeString) return 0;

  const [hours, minutes] = timeString.split(':').map(Number);
  return (hours * 60 + minutes) * 60 * 1000;
};

// Format gap time using humanize-duration
const formatGapTime = (waitTime: string | undefined, duration: string | undefined): string | null => {
  const timeValue = waitTime || duration;
  if (!timeValue) return null;

  const ms = timeStringToMs(timeValue);
  if (ms === 0) return null;

  return humanizeDuration(ms, {
    round: true,
    largest: 2,
    delimiter: ' ',
    spacer: ' '
  });
};

const ExperimentProtocolPreview: React.FC<ExperimentProtocolPreviewProps> = ({
  protocolData,
  className
}) => {
  const [collapsedDays, setCollapsedDays] = useState(new Set<number>(protocolData.days?.map((day, index) => index) || []));

  const toggleDay = (day: number) => {
    const newCollapsed = new Set(collapsedDays);
    if (newCollapsed.has(day)) {
      newCollapsed.delete(day);
    } else {
      newCollapsed.add(day);
    }
    setCollapsedDays(newCollapsed);
  };

  const days = protocolData.days || [];

  return (
    <div className={`space-y-2 ${className || ''}`}>
      {days.length > 0 ? (
        days.map((day, dayIndex) => (
          <Collapsible
            key={dayIndex}
            open={collapsedDays.has(dayIndex)}
            onOpenChange={() => toggleDay(dayIndex)}
          >
            <CollapsibleTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-between bg-yellow-400 hover:bg-yellow-500 text-black font-semibold py-3 rounded-full"
              >
                {day.title}
                {collapsedDays.has(dayIndex) ? (
                  <ChevronRight className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-0 mt-4">
            <div className="text-sm mb-2 prose prose-sm max-w-none prose-p:text-gray-600">
              <ReactMarkdown
                components={{
                  a: ({ node, ...props }) => (
                    <a {...props} className="text-blue-600 underline hover:text-blue-700" />
                  ),
                }}
              >
                {day.description || ""}
              </ReactMarkdown>
            </div>
              {day.actions?.map((action, actionIndex) => {
                const instructions = getStepInstructions(action);
                const isLastStep = actionIndex === day.actions.length - 1;
                return (
                  <div
                    key={actionIndex}
                    className="flex items-start gap-4 p-4 relative"
                  >
                    {/* Vertical line that passes through icon centers */}
                    <div
                      className="absolute left-[124px] top-0 bottom-0 w-1 bg-gray-100"
                      style={{
                        top: actionIndex === 0 ? "60px" : "0px",
                        bottom: isLastStep ? "calc(100% - 60px)" : "0px",
                      }}
                    />

                    <div className="flex flex-row items-center min-w-[80px] relative z-10 bg-white gap-4">
                      <div className="text-sm font-medium w-[64px] block text-right">
                        {getModelTime(
                          action.meta?.model_time as string | undefined
                        )}
                      </div>
                      <div
                        className="size-[60px] rounded-full border-2 border-gray-100 bg-gray-100 flex items-center justify-center"
                        data-step-icon={action.type}
                      >
                        {getStepIcon(action.type) && (
                          <img
                            src={getStepIcon(action.type)}
                            alt={action.type}
                            className="size-9"
                          />
                        )}
                      </div>
                    </div>

                    <div className="flex-1">
                      <h4 className="font-semibold text-base mb-1">
                        {action.title}
                      </h4>
                      <p className="text-sm text-gray-600 mb-2 whitespace-pre-wrap">
                        {action.description}
                      </p>

                      {/* Show label for review type actions */}
                      {action.type === "review" && action.meta?.label && (
                        <p className="text-xs text-muted-foreground italic mb-2">
                          "{String(action.meta.label)}"
                        </p>
                      )}

                      {/* Show gap time if wait_time or duration exists */}
                      {(() => {
                        const gapTime = formatGapTime(
                          action.meta?.wait_time as string | undefined,
                          action.meta?.duration as string | undefined
                        );
                        return gapTime ? (
                          <div className="text-sm text-muted-foreground italic mb-2 border-t pt-2">
                            Gap from last log: {gapTime}
                          </div>
                        ) : null;
                      })()}

                      {action.type === "product" && (
                        <p className="text-sm text-gray-600 mb-2 whitespace-pre-wrap">
                          {Array.isArray(action.meta?.product_ids) && action.meta.product_ids.length > 0 && (
                            <span>(Products: {action.meta.product_ids.join(', ')})</span>
                          )}
                        </p>
                      )}

                      {instructions.length > 0 && (
                        <ul className="text-xs text-gray-500 space-y-1 ml-4">
                          {instructions.map((instruction, idx) => (
                            <li key={idx}>• {instruction}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                );
              })}
            </CollapsibleContent>
          </Collapsible>
        ))
      ) : (
        <div className="text-center py-6 text-muted-foreground">
          No steps defined yet.
        </div>
      )}
    </div>
  );
};

export default ExperimentProtocolPreview;